import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Factory, Clock, CheckCircle2, AlertTriangle, ArrowLeft, Radio } from 'lucide-react'
import { api } from '@/lib/api'

interface Andon {
  atualizado_em: string
  em_producao: Array<{
    op_id: number
    numero: number
    produto_codigo: string
    produto_descricao: string
    variante: string
    quantidade: number
    maquina_id: number | null
    maquina_nome: string | null
    etapas_total: number
    etapas_concluidas: number
    progresso_pct: number
    etapa_atual: {
      id?: number;
      codigo: string; nome: string; cor: string;
      iniciada_em: string | null; pausada_em: string | null;
      duracao_min: number | null; sla_min: number;
      estouro_pct: number | null;
      operador_id: number | null; operador_nome: string | null;
      quantidade_ok?: number; peso_ok?: number;
    } | null
    tempo_rodando_min: number | null
    atrasada: boolean
  }>
  fila: Record<string, { ops: number; qtd_total: number }>
  hoje: {
    ops_concluidas: number
    quantidade_produzida: number
    quantidade_refugo: number
  }
}

function fmtDuration(min: number | null | undefined): string {
  if (!min && min !== 0) return '--'
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${h}h${m.toString().padStart(2, '0')}m`
}

export default function AndonIndustrial() {
  const qc = useQueryClient()
  const [wsConnected, setWsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const [refreshSec, setRefreshSec] = useState(() => {
    const saved = localStorage.getItem('andon_refresh_sec')
    return saved ? Number(saved) : 3
  })

  useEffect(() => {
    localStorage.setItem('andon_refresh_sec', String(refreshSec))
  }, [refreshSec])

  const { data } = useQuery<Andon>({
    queryKey: ['andon'],
    queryFn: async () => (await api.get('/industrial/andon')).data,
    refetchInterval: refreshSec * 1000,
  })

  // Relógio local que atualiza a cada segundo (pros cronômetros das etapas)
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // WebSocket — invalida query a cada evento de produção/lote/sos
  useEffect(() => {
    const token = localStorage.getItem('pain_token')
    if (!token) return
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${proto}//${window.location.host}/api/industrial/andon/stream?token=${encodeURIComponent(token)}`
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => setWsConnected(true)
    ws.onclose = () => setWsConnected(false)
    ws.onerror = () => setWsConnected(false)
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data)
        // qualquer evento de produção/lote/sos invalida o snapshot
        if (msg.evento && (
          msg.evento.startsWith('producao.') ||
          msg.evento.startsWith('lote.') ||
          msg.evento.startsWith('andon.')
        )) {
          qc.invalidateQueries({ queryKey: ['andon'] })
        }
      } catch {}
    }
    return () => { ws.close() }
  }, [qc])

  const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="min-h-screen bg-ink-950 text-white p-6 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/industrial/dashboard" className="text-ink-400 hover:text-white">
            <ArrowLeft size={20} />
          </Link>
          <Factory className="text-brand-400" size={28} />
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              Painel de Produção
              <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${wsConnected ? 'bg-emerald-700 text-emerald-100' : 'bg-amber-700 text-amber-100'}`}>
                <Radio className={`h-3 w-3 ${wsConnected ? 'animate-pulse' : ''}`} />
                {wsConnected ? 'live' : 'reconectando...'}
              </span>
            </h1>
            <div className="text-sm text-ink-400 flex items-center gap-3 flex-wrap">
              <span>tempo real · última: {data ? new Date(data.atualizado_em).toLocaleTimeString('pt-BR') : '—'}</span>
              <select
                value={refreshSec}
                onChange={e => setRefreshSec(Number(e.target.value))}
                className="bg-ink-800 border border-ink-600 rounded px-2 py-0.5 text-xs"
              >
                <option value={3}>3s</option>
                <option value={5}>5s</option>
                <option value={10}>10s</option>
                <option value={30}>30s</option>
                <option value={60}>60s</option>
              </select>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-mono font-bold">{hora}</div>
          <div className="text-sm text-ink-400">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</div>
        </div>
      </div>

      {/* Resumo do dia */}
      {data && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <ResumoCard label="Em produção agora" value={data.em_producao.length.toString()} color="bg-brand-600" />
          <ResumoCard
            label="Concluídas hoje"
            value={data.hoje.ops_concluidas.toString()}
            sub={`${data.hoje.quantidade_produzida.toFixed(0)} un produzidas`}
            color="bg-emerald-600"
          />
          <ResumoCard
            label="Refugo hoje"
            value={data.hoje.quantidade_refugo.toFixed(0)}
            color={data.hoje.quantidade_refugo > 0 ? 'bg-rose-600' : 'bg-ink-600'}
          />
          <ResumoCard
            label="Fila (propostas + planejadas)"
            value={((data.fila.proposta?.ops || 0) + (data.fila.planejada?.ops || 0)).toString()}
            sub={`${((data.fila.proposta?.qtd_total || 0) + (data.fila.planejada?.qtd_total || 0)).toFixed(0)} un aguardando`}
            color="bg-amber-600"
          />
        </div>
      )}

      {/* Cards de OPs em produção */}
      <div className="flex-1">
        <h2 className="text-xl font-semibold mb-3 text-ink-300">OPs em execução</h2>
        {!data || data.em_producao.length === 0 ? (
          <div className="bg-ink-900 rounded-lg p-16 text-center">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={64} />
            <div className="text-2xl font-semibold text-ink-300">Nenhuma OP em execução</div>
            <div className="text-ink-500 mt-2">Fábrica parada ou sem OPs iniciadas.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.em_producao.map(op => (
              <OPCard key={op.op_id} op={op} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ResumoCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div className={`${color} rounded-lg p-4 shadow-lg`}>
      <div className="text-xs uppercase opacity-80">{label}</div>
      <div className="text-4xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-90 mt-1">{sub}</div>}
    </div>
  )
}

function OPCard({ op }: { op: Andon['em_producao'][number] }) {
  const atraso = op.etapa_atual?.estouro_pct !== null && op.etapa_atual?.estouro_pct !== undefined && op.etapa_atual.estouro_pct > 20
  const borderColor = op.atrasada ? 'border-rose-500' : atraso ? 'border-amber-500' : 'border-ink-700'
  const progresso = op.progresso_pct

  return (
    <div className={`bg-ink-900 rounded-lg border-2 ${borderColor} overflow-hidden shadow-xl`}>
      {/* Header */}
      <div className="p-3 flex items-center justify-between bg-ink-800">
        <div>
          <div className="text-xs text-ink-400">OP</div>
          <div className="text-2xl font-mono font-bold">#{op.numero}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-ink-400">Máquina</div>
          <div className="font-semibold">{op.maquina_nome || '—'}</div>
        </div>
      </div>

      {/* Produto */}
      <div className="p-3 border-b border-ink-800">
        <div className="text-xs text-ink-400 font-mono">{op.produto_codigo}</div>
        <div className="text-lg font-semibold leading-tight">{op.produto_descricao}</div>
        <div className="flex gap-2 mt-1 text-xs">
          <span className="bg-ink-700 px-2 py-0.5 rounded">{op.variante}</span>
          <span className="bg-brand-700 px-2 py-0.5 rounded">{op.quantidade} un</span>
        </div>
      </div>

      {/* Etapa atual */}
      {op.etapa_atual ? (
        <div className="p-3 border-b border-ink-800" style={{ backgroundColor: op.etapa_atual.cor + '22' }}>
          <div className="text-xs text-ink-400">Etapa atual</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs"
              style={{ backgroundColor: op.etapa_atual.cor, color: 'white' }}>
              {op.etapas_concluidas + 1}
            </div>
            <div className="flex-1">
              <div className="font-semibold flex items-center gap-2">
                {op.etapa_atual.nome}
                {op.etapa_atual.pausada_em && (
                  <span className="text-[10px] bg-amber-600 px-1.5 py-0.5 rounded font-bold">⏸ PAUSADA</span>
                )}
              </div>
              {op.etapa_atual.operador_nome && (
                <div className="text-xs text-emerald-300 font-medium">👤 {op.etapa_atual.operador_nome}</div>
              )}
              <div className="text-xs text-ink-400 flex items-center gap-1">
                <Clock size={10} /> rodando há {fmtDuration(op.etapa_atual.duracao_min)}
                {op.etapa_atual.sla_min > 0 && ` / SLA ${fmtDuration(op.etapa_atual.sla_min)}`}
              </div>
            </div>
            {atraso && (
              <div className="text-rose-400 flex items-center gap-1 text-sm font-bold">
                <AlertTriangle size={16} />
                +{op.etapa_atual.estouro_pct}%
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-3 border-b border-ink-800 text-ink-500 text-sm">
          Nenhuma etapa em execução — abra a OP pra iniciar a próxima.
        </div>
      )}

      {/* Progresso */}
      <div className="p-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-ink-400">Progresso do roteiro</span>
          <span className="font-bold">{op.etapas_concluidas}/{op.etapas_total}</span>
        </div>
        <div className="w-full bg-ink-700 rounded-full h-2 overflow-hidden">
          <div className="bg-emerald-500 h-full transition-all" style={{ width: `${progresso}%` }} />
        </div>
        <div className="text-xs text-ink-500 mt-2 flex items-center justify-between">
          <span>OP ativa há {fmtDuration(op.tempo_rodando_min)}</span>
          {op.atrasada && <span className="text-rose-400 font-bold">ATRASADA</span>}
        </div>
      </div>
    </div>
  )
}
